import EventList from "../../Events/EventList";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetEventsQuery } from "../../services";
import { QIErrorBoundary } from "../../../../components";
import moment from "moment";
import "./style.scss";
import { getLocalizedString } from "../../../../shared/translation";

export const EventsContainer = ({
  resetCount,
  setResetCount,
  deviceId,
  selectedRange,
  setSelectedRange,
  clientId,
  selectedVehiclePlate,
  type,
  setDeviceId = () => {},
  setMapPoint = () => {},
  setDockAlign = () => {},
  setShowEventsDetails = () => {},
}) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [eventSearch, setEventSearch] = useState("");
  const [selectedId, setSelectedId] = useState();

  const [eventsList, setEventsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const processedPagesRef = useRef(new Set());
  const loadMoreLockedRef = useRef(false);

  const rangeStart = useMemo(
    () => moment(selectedRange.startDate).valueOf(),
    [selectedRange.startDate]
  );
  const rangeEnd = useMemo(() => moment(selectedRange.endDate).valueOf(), [selectedRange.endDate]);

  // Context uniquely identifies the current data set. Changing it resets the
  // accumulated rows so pages from a previous selection are never mixed in.
  const dataContext = `${type}:${deviceId || ""}:${selectedVehiclePlate || ""}:${rangeStart}:${rangeEnd}:${perPage}`;

  useEffect(() => {
    setEventsList([]);
    setTotalCount(0);
    setPage(1);
    setSelectedId(undefined);
    processedPagesRef.current.clear();
    loadMoreLockedRef.current = false;
  }, [dataContext]);

  //Get Events data
  const {
    data: eventsData,
    error,
    isSuccess,
    isFetching,
  } = useGetEventsQuery(
    deviceId && {
      startTime: rangeStart,
      endTime: rangeEnd,
      deviceIds: deviceId,
      clientId: clientId,
      plate_number: [selectedVehiclePlate],
      source_id: [deviceId],
      type: type,
      search: false,
      //Page count starts from zero.
      page: page - 1,
      perPage: perPage,
      q: eventSearch.toUpperCase(),
    }
  );

  // Accumulate pages instead of replacing. Each page is processed once, guarded
  // by processedPagesRef so React re-renders cannot duplicate rows.
  useEffect(() => {
    if (!eventsData || !isSuccess || isFetching || !deviceId) return;

    loadMoreLockedRef.current = false;
    setTotalCount(eventsData.total_count || 0);

    const processedPageKey = `${dataContext}:${page}`;
    if (processedPagesRef.current.has(processedPageKey)) return;
    processedPagesRef.current.add(processedPageKey);

    if (eventsData?.events?.length) {
      setEventsList((previousRows) => [...previousRows, ...eventsData.events]);
    }
  }, [eventsData, isSuccess, isFetching, deviceId, dataContext, page]);

  // Select the first event and position the map once the first page loads.
  useEffect(() => {
    if (page !== 1) return;

    if (eventsData?.events?.length > 0) {
      const firstEvent = eventsData?.events[0];
      try {
        setDeviceId(firstEvent?.source_id);
        setShowEventsDetails(firstEvent);
        setSelectedId(0);

        if (
          firstEvent?.events?.type === "geofence" &&
          ["exit", "entry"].includes(firstEvent?.events?.status)
        ) {
          setMapPoint(
            firstEvent?.events[
              firstEvent?.events?.status === "entry" ? "entry_position" : "exit_position"
            ]
          );
        } else {
          setMapPoint(firstEvent?.gps?.position);
        }
        if (
          firstEvent?.events?.type === "geofence" &&
          firstEvent?.events?.sub_type === "time limit exceeded"
        ) {
          setMapPoint(firstEvent?.events?.start_position);
        }
      } catch (error) {
        console.log(error);
      }
    } else if (isSuccess && !isFetching) {
      setMapPoint(null);
    }
  }, [eventsData?.events, page, isSuccess, isFetching]);

  useEffect(() => {
    if (error) {
      loadMoreLockedRef.current = false;
    }
  }, [error]);

  const hasMore = eventsList.length < totalCount;

  const handleLoadMore = useCallback(() => {
    if (loadMoreLockedRef.current || isFetching || !hasMore) return;
    loadMoreLockedRef.current = true;
    setPage((currentPage) => currentPage + 1);
  }, [hasMore, isFetching]);

  const handlePerPageChange = useCallback((nextPerPage) => {
    setPerPage(nextPerPage);
  }, []);

  const message = {
    header: getLocalizedString("no_device_selected", "No Device Selected"),
    content: getLocalizedString("please_select_a_device", "Please Select a Device"),
  };

  return deviceId ? (
    <EventList
      data={{ events: eventsList, total_count: totalCount }}
      resetCount={resetCount}
      isSuccess={isSuccess}
      isFetching={isFetching && eventsList.length === 0}
      setResetCount={setResetCount}
      selectedRange={selectedRange}
      setSelectedRange={setSelectedRange}
      error={error}
      listType={"Events"}
      batchSize={perPage}
      onBatchSizeChange={handlePerPageChange}
      hasMore={hasMore}
      isLoadingMore={isFetching}
      onLoadMore={handleLoadMore}
      setDeviceId={setDeviceId}
      setMapPoint={setMapPoint}
      setDockAlign={setDockAlign}
      selectedId={selectedId}
      setShowEventsDetails={setShowEventsDetails}
    />
  ) : (
    <QIErrorBoundary message={message} />
  );
};
